import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Phone, Clock, Navigation, Anchor } from "lucide-react";

// --- Mapa do Brasil (STANDBY — comentado para uso futuro) ---
// import BrazilMap from "./BrazilMap";
// Para reativar o mapa, descomentar o import acima e substituir
// o grid de UnitCardMini pelo componente:
// <BrazilMap className="w-full h-auto text-primary" />



interface UnitData {
  name: string;
  state: string;
  address: string;
  phone: string;
  whatsapp: string;
  services: string[];
  highlight?: string;
}

const units: UnitData[] = [
  {
    name: "São Francisco do Sul",
    state: "Santa Catarina",
    address: "Lgo. Almirante Moraes Rego, 156 — Centro, São Francisco do Sul/SC",
    phone: "(47) 99206-9885",
    whatsapp: `https://wa.me/5547992069885?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de São Francisco do Sul.")}`,
    services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
    highlight: "Sede",
  },
  {
    name: "Barra do Sul",
    state: "Santa Catarina",
    address: "Av. Amândio Cabral, 491 — Centro, Balneário Barra do Sul/SC",
    phone: "(47) 99206-9885",
    whatsapp: `https://wa.me/5547992069885?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de Barra do Sul.")}`,
    services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
    highlight: "Filial",
  },
  {
    name: "Balneário Camboriú",
    state: "Santa Catarina",
    address: "R. Dom Henrique, 1200 — Jardim Iate Clube, Balneário Camboriú/SC",
    phone: "(47) 99146-8851",
    whatsapp: `https://wa.me/5547991468851?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de Balneário Camboriú.")}`,
    services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
    highlight: "Filial",
  },
  {
    name: "Canoinhas",
    state: "Santa Catarina",
    address: "Rua Irmã Maria Felicitas, 631 — Centro, Canoinhas/SC",
    phone: "(47) 99146-8851",
    whatsapp: `https://wa.me/5547991468851?text=${encodeURIComponent("Olá! Gostaria de informações sobre a unidade de Canoinhas.")}`,
    services: ["Escola Náutica", "Despachante Náutico", "Consultoria"],
    highlight: "Filial",
  },
];

const UnitCardMini = ({ unit, index }: { unit: UnitData; index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.45, delay: index * 0.1 }}
    className="group relative bg-white rounded-xl border border-slate-200 p-5 hover:border-gold/30 hover:shadow-lg hover:shadow-gold/5 transition-all duration-400"
  >
    {/* Highlight badge */}
    {unit.highlight && (
      <span className="absolute -top-2.5 right-4 bg-gold text-primary text-[9px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-widest shadow-sm">
        {unit.highlight}
      </span>
    )}

    {/* Header */}
    <div className="flex items-start justify-between mb-3">
      <div>
        <h4 className="font-heading text-base font-semibold text-primary group-hover:text-gold transition-colors duration-300">
          {unit.name}
        </h4>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
          {unit.state}
        </span>
      </div>
      <Anchor className="w-4 h-4 text-gold/40 group-hover:text-gold/70 transition-colors shrink-0 mt-0.5" />
    </div>

    {/* Info */}
    <div className="space-y-1.5 mb-3">
      <div className="flex items-start gap-2">
        <MapPin className="w-3.5 h-3.5 text-gold mt-0.5 shrink-0" />
        <span className="text-xs text-muted-foreground leading-relaxed">{unit.address}</span>
      </div>
      <div className="flex items-center gap-2">
        <Phone className="w-3.5 h-3.5 text-gold shrink-0" />
        <span className="text-xs text-muted-foreground">{unit.phone}</span>
      </div>
    </div>

    {/* Service Tags */}
    <div className="flex flex-wrap gap-1 mb-4">
      {unit.services.map((s) => (
        <span
          key={s}
          className="text-[9px] font-medium text-primary/60 bg-primary/5 px-2 py-0.5 rounded-full border border-primary/8"
        >
          {s}
        </span>
      ))}
    </div>

    {/* CTAs */}
    <div className="flex gap-2">
      <a
        href={unit.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
          <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
        </svg>
        WhatsApp
      </a>
      <a
        href={`https://www.google.com/maps/search/${encodeURIComponent(unit.address)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-xs font-medium text-muted-foreground hover:border-gold/30 hover:text-gold transition-all"
      >
        <Navigation className="w-3 h-3" />
        Rota
      </a>
    </div>
  </motion.div>
);

const AboutUsSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section ref={ref} id="unidades" className="py-24 bg-background">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* Lado esquerdo — Texto */}
          <div className="max-w-xl lg:sticky lg:top-32">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="text-sm font-semibold text-accent uppercase tracking-widest">
                Presença Nacional
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mt-4 mb-6">
                4 Bases.{" "}
                <span className="text-accent">Uma Missão.</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                De Balneário Camboriú a Canoinhas, nossas unidades estão posicionadas nos principais polos náuticos de Santa Catarina. Presença física onde o armador precisa — no cais, no porto, na capitania.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-4">
                São Francisco do Sul, Barra do Sul, Balneário Camboriú e Canoinhas. Cada base opera com a mesma excelência: formação náutica de elite e assessoria documental sem burocracia.
              </p>
              <a
                href="/unidades"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-xl bg-gold text-primary font-medium text-sm hover:bg-gold/90 transition-colors shadow-lg"
              >
                Ver Todas as Unidades
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </motion.div>
          </div>

          {/* Lado direito — Carrossel no mobile / Grid 2x2 no desktop */}
          <div>
            {/* Desktop: Grid 2x2 */}
            <div className="hidden sm:grid sm:grid-cols-2 gap-4">
              {units.map((unit, i) => (
                <UnitCardMini key={unit.name} unit={unit} index={i} />
              ))}
            </div>

            {/* Mobile: Carrossel horizontal com snap */}
            <div className="sm:hidden">
              <div
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory pt-3 pb-4 -mx-2 px-2 scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                ref={(el) => {
                  if (el) {
                    // Observer para atualizar dots
                    const cards = el.querySelectorAll('[data-unit-card]');
                    const observer = new IntersectionObserver(
                      (entries) => {
                        entries.forEach((entry) => {
                          if (entry.isIntersecting) {
                            const idx = (entry.target as HTMLElement).dataset.unitIndex;
                            const dots = document.querySelectorAll('[data-dot]');
                            dots.forEach((dot, di) => {
                              (dot as HTMLElement).style.opacity = di === Number(idx) ? '1' : '0.3';
                              (dot as HTMLElement).style.width = di === Number(idx) ? '20px' : '6px';
                            });
                          }
                        });
                      },
                      { root: el, threshold: 0.6 }
                    );
                    cards.forEach((card) => observer.observe(card));
                  }
                }}
              >
                {units.map((unit, i) => (
                  <div
                    key={unit.name}
                    data-unit-card
                    data-unit-index={i}
                    className="snap-center shrink-0"
                    style={{ width: '85%' }}
                  >
                    <UnitCardMini unit={unit} index={0} />
                  </div>
                ))}
              </div>

              {/* Dots indicator */}
              <div className="flex justify-center gap-1.5 mt-3">
                {units.map((_, i) => (
                  <div
                    key={i}
                    data-dot
                    className="h-1.5 rounded-full bg-gold transition-all duration-300"
                    style={{
                      width: i === 0 ? '20px' : '6px',
                      opacity: i === 0 ? 1 : 0.3,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AboutUsSection;
